import { Box, Button, Flex } from "@chakra-ui/react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { ConnectedProps, connect, useSelector } from "react-redux";

import { RanaModal } from "@nens/ranalib";

import MoreInfoIcon from "../icons/more_info.svg?react";
import { getTwoDFlowSpeedLayer, setTwoDFlowSpeedLayer } from "../state/layerCalculationMenu";

import LayerSubMenu from "./LayerSubMenu";
import WaterFlowSettingItem from "./WaterflowSettingItem";

const LayerMenuCalculationLayerTwoDWaterflows: React.FC<ConnectedProps<typeof connector>> = (
  props
) => {
  const twoDFlowLayerConfig = useSelector(getTwoDFlowSpeedLayer);

  const currentLayerVisible = twoDFlowLayerConfig.visible;

  const [showFlowSettings, setShowFlowSettings] = useState(false);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const openMoreInfoModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowInfoModal(true);
  };

  const formattedMessageColorPicker = (
    <FormattedMessage id="model.color_picker" defaultMessage="Color Picker" />
  );
  const formattedMessageLayerName = (
    <FormattedMessage id="layerMenu.2d_flow_velocity_title" defaultMessage="Flow Velocity (2D)" />
  );

  const colorPickerHeader = (
    <div>
      {formattedMessageColorPicker}
      {": "}
      {formattedMessageLayerName}
    </div>
  );

  return (
    <>
      <LayerSubMenu.Item
        active={currentLayerVisible}
        title={"Flow Velocity (2D)"}
        onClick={() => {
          props.setTwoDFlowSpeedLayer({ visible: !currentLayerVisible });
          if (currentLayerVisible) setShowFlowSettings(false);
        }}
        disabledMessage={
          twoDFlowLayerConfig.disabled ? "Model is too large to show 2D flow" : undefined
        }
      >
        <Flex alignItems="center" gap={1}>
          <LayerSubMenu.Item.Name>{formattedMessageLayerName}</LayerSubMenu.Item.Name>
          <Box onClick={openMoreInfoModal} cursor="pointer">
            <MoreInfoIcon />
          </Box>
        </Flex>

        <LayerSubMenu.OpenSubMenuButton
          active={showFlowSettings}
          onClick={() => {
            setShowFlowSettings(!showFlowSettings);
            if (!showFlowSettings) props.setTwoDFlowSpeedLayer({ visible: true });
          }}
        />
      </LayerSubMenu.Item>

      {showFlowSettings && (
        <>
          <WaterFlowSettingItem
            title="Set the color of the flow animation"
            label="Color"
            colorPickerHeader={colorPickerHeader}
            defaultValue={twoDFlowLayerConfig.color}
            onChange={(value: string) => props.setTwoDFlowSpeedLayer({ color: value })}
          />
          <WaterFlowSettingItem
            title="Set the speed of the flow animation"
            label="Speed"
            slider
            defaultValue={twoDFlowLayerConfig.speedPercentage}
            onChange={(value: number) => props.setTwoDFlowSpeedLayer({ speedPercentage: value })}
          />
          <WaterFlowSettingItem
            title="Set the amount of drops"
            label="Amount"
            slider
            defaultValue={twoDFlowLayerConfig.amountPercentage}
            onChange={(value: number) => props.setTwoDFlowSpeedLayer({ amountPercentage: value })}
          />
          <WaterFlowSettingItem
            title="Set the size of the drops"
            label="Size"
            slider
            defaultValue={twoDFlowLayerConfig.sizePercentage}
            onChange={(value: number) => props.setTwoDFlowSpeedLayer({ sizePercentage: value })}
          />
          <WaterFlowSettingItem
            title="Flow animation is not shown if velocity is below this value"
            label="Min velocity"
            defaultValue={twoDFlowLayerConfig.waterflowThreshold}
            onChange={(value: number) => props.setTwoDFlowSpeedLayer({ waterflowThreshold: value })}
          />
        </>
      )}
      {showInfoModal ? (
        <RanaModal
          isOpen={true}
          onClose={() => setShowInfoModal(false)}
          topBar={formattedMessageLayerName}
          maxW="400px"
          buttons={[
            <Button variant="primary" onClick={() => setShowInfoModal(false)}>
              OK
            </Button>,
          ]}
        >
          The visualisation of 2D flow is based on the flow velocity at the center of the
          computational grid cells. It does not take into account intra-cell variation of flow
          velocity or direction. For this reason, you may see flow in dry parts of cells. It is not
          recommended to use this visualisation in flat areas with low water depths.
        </RanaModal>
      ) : null}
    </>
  );
};

const connector = connect(null, {
  setTwoDFlowSpeedLayer,
});

export default connector(LayerMenuCalculationLayerTwoDWaterflows);
